import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from '../../components/admin/AdminLayout';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Building2, Save, Loader2, Plus, Trash2, Upload } from 'lucide-react';
import ParentTabEditor from '../../components/admin/itmu/ParentTabEditor';
import CommercializationTabEditor from '../../components/admin/itmu/CommercializationTabEditor';
import IPRProtectionTabEditor from '../../components/admin/itmu/IPRProtectionTabEditor';
import ProfessionalServiceTabEditor from '../../components/admin/itmu/ProfessionalServiceTabEditor';
import EventsTabEditor from '../../components/admin/itmu/EventsTabEditor';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function ITMUManagementPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('parent');

  useEffect(() => {
    fetchData();
  }, []);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('cish_token');
    return { Authorization: `Bearer ${token}` };
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/itmu-ipr`);
      setData(response.data);
    } catch (error) {
      console.error('Error fetching ITMU data:', error);
      toast.error('Failed to load ITMU data');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await axios.put(
        `${API_URL}/api/itmu-ipr`,
        data,
        { headers: getAuthHeaders() }
      );
      toast.success('ITMU data saved successfully!');
      await fetchData();
    } catch (error) {
      console.error('Error saving ITMU data:', error);
      toast.error(error.response?.data?.detail || 'Failed to save ITMU data');
    } finally {
      setSaving(false);
    }
  };

  const updateData = (section, value) => {
    setData(prev => ({
      ...prev,
      [section]: value
    }));
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-[#0E6D2B]" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Building2 className="w-8 h-8 text-[#0E6D2B]" />
            <div>
              <h1 className="text-2xl font-bold text-gray-800">ITMU / IPR Cell Management</h1>
              <p className="text-sm text-gray-600">Manage all ITMU and IPR Cell content</p>
            </div>
          </div>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-[#0E6D2B] hover:bg-[#0a5a23]"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Save All Changes
          </Button>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid grid-cols-5 w-full h-auto">
            <TabsTrigger value="parent" className="py-3">Parent</TabsTrigger>
            <TabsTrigger value="commercialization" className="py-3">Commercialization</TabsTrigger>
            <TabsTrigger value="ipr" className="py-3">IPR Protection</TabsTrigger>
            <TabsTrigger value="professional" className="py-3">Professional Service</TabsTrigger>
            <TabsTrigger value="events" className="py-3">Events</TabsTrigger>
          </TabsList>

          <TabsContent value="parent">
            <ParentTabEditor
              data={data?.parent}
              onChange={(value) => updateData('parent', value)}
            />
          </TabsContent>

          <TabsContent value="commercialization">
            <CommercializationTabEditor
              data={data?.commercialization}
              onChange={(value) => updateData('commercialization', value)}
            />
          </TabsContent>

          <TabsContent value="ipr">
            <IPRProtectionTabEditor
              data={data?.ipr_protection}
              onChange={(value) => updateData('ipr_protection', value)}
            />
          </TabsContent>

          <TabsContent value="professional">
            <ProfessionalServiceTabEditor
              data={data?.professional_service}
              onChange={(value) => updateData('professional_service', value)}
            />
          </TabsContent>

          <TabsContent value="events">
            <EventsTabEditor
              data={data?.events}
              onChange={(value) => updateData('events', value)}
            />
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
}
